import React, { useState, useEffect } from "react";
import { Edit3, Trash2, Info, FileText, Download, Send, Search, X, Briefcase } from "lucide-react";
import { Modal } from "./Modal";
import { Loader } from "./PageHead";
import { DisciplineBadge, PortalBadge } from "./Badges";
import { MoveEquipmentToJobModal } from "./MoveEquipmentToJobModal";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";

async function openBlob(path) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function Field({ label, value }) {
  return <div className="fld"><label>{label}</label><div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{value || "—"}</div></div>;
}

export function EquipmentModal({ id, canEdit, onClose, onChanged, onOpenJob }) {
  const { user } = useAuth();
  const isCustomer = user?.user_type === "customer";
  const [eq, setEq] = useState(null);
  const [mode, setMode] = useState("view");
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("Beyond economical repair");
  const [otherReason, setOtherReason] = useState("");
  const [docs, setDocs] = useState([]);
  const [quoting, setQuoting] = useState(null);
  const [openReadings, setOpenReadings] = useState(null);
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [movingToJob, setMovingToJob] = useState(false);
  const [dnJobs, setDnJobs] = useState([]);

  useEffect(() => {
    api.get(`/equipment/${id}`).then((r) => { setEq(r.data); setForm(r.data); }).catch((e) => toast.error(apiErr(e)));
    api.get(`/equipment/${id}/documents`).then((r) => setDocs(r.data || [])).catch(() => {});
  }, [id]);

  const downloadDoc = async (docId) => {
    try { await openBlob(`/equipment-documents/${docId}/download`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const requestReplacement = async (d) => {
    setQuoting(d.id);
    try {
      const r = await api.post(`/equipment/${id}/request-replacement-quote`,
        { report_number: d.report_number });
      toast.success(`Replacement quote requested — ${r.data.reference}. Our team will be in touch.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setQuoting(null); }
  };

  useEffect(() => {
    api.get(`/equipment/${id}`).then((r) => { setEq(r.data); setForm(r.data); }).catch((e) => toast.error(apiErr(e)));
  }, [id]);

  useEffect(() => {
    if (custQ.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      api.get("/customers", { params: { search: custQ, limit: 50 } })
        .then((r) => setCustResults(r.data.items || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [custQ]);

  // Staff-only: look up the job(s) matching this equipment's last DN so a
  // "close out this DN" shortcut can jump straight to the full job (all its
  // line items) instead of forcing a one-item-at-a-time flow.
  useEffect(() => {
    setDnJobs([]);
    if (isCustomer || !onOpenJob || !eq?.delivery_note_number) return;
    api.get("/jobs/duplicate-check", { params: { delivery_note_number: eq.delivery_note_number } })
      .then((r) => setDnJobs(r.data?.matches || [])).catch(() => {});
  }, [isCustomer, onOpenJob, eq?.delivery_note_number]);

  const isManager = user?.role === "manager" || user?.role === "admin";

  const saveEdit = async () => {
    try {
      const body = {
        serial_number: form.serial_number, asset_number: form.asset_number,
        issue_number: form.issue_number,
        part_number: form.part_number, description: form.description, manufacturer: form.manufacturer,
        calibration_period_days: form.calibration_period_days ? Number(form.calibration_period_days) : null,
        customer_id: form.customer_id || null,
      };
      if (isManager) {
        body.last_calibration_date = form.last_calibration_date || null;
        body.calibration_due_date = form.calibration_due_date || null;
        body.delivery_note_number = form.delivery_note_number || null;
      }
      await api.patch(`/equipment/${id}`, body);
      toast.success("Equipment updated — change written to audit log");
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doScrap = async () => {
    const finalReason = reason === "Other…" ? otherReason.trim() : reason;
    if (!finalReason) { toast.error("Please give a reason — this equipment will be marked unserviceable."); return; }
    try {
      await api.post(`/equipment/${id}/scrap`, { reason: finalReason });
      toast.success("Marked Unserviceable — removed from recall, kept in history");
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!eq) return <Modal open title="Loading…" onClose={onClose}><Loader /></Modal>;

  const footer = canEdit && mode === "view" ? (
    <>
      {!eq.scrapped && <button className="btn ghost" style={{ color: "var(--red)", borderColor: "#f3d6d4" }} onClick={() => setMode("scrap")} data-testid="equip-scrap-open"><Trash2 size={15} /> Scrap</button>}
      <button className="btn ghost" onClick={() => setMovingToJob(true)} data-testid="equip-add-to-job-open"><Briefcase size={15} /> Add to Job</button>
      <button className="btn ghost" onClick={() => setMode("edit")} data-testid="equip-edit-open"><Edit3 size={15} /> Edit</button>
      <button className="btn gold" onClick={onClose}>Done</button>
    </>
  ) : mode === "edit" ? (
    <><button className="btn ghost" onClick={() => setMode("view")}>Cancel</button><button className="btn gold" onClick={saveEdit} data-testid="equip-edit-save">Save changes</button></>
  ) : mode === "scrap" ? (
    <><button className="btn ghost" onClick={() => setMode("view")}>Cancel</button><button className="btn danger" onClick={doScrap} data-testid="equip-scrap-confirm">Confirm & exclude from recall</button></>
  ) : <button className="btn gold" onClick={onClose}>Done</button>;

  return (
    <>
    <Modal open onClose={onClose} title={eq.description || "Equipment"}
      subtitle={`${eq.manufacturer || ""}${eq.manufacturer && eq.customer_name ? " · " : ""}${eq.customer_name || ""}`} footer={footer}>
      {mode === "view" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <DisciplineBadge name={eq.discipline} /><PortalBadge status={eq.portal_status} />
            {eq.data_quality_flag && <span className="badge b-amber"><span className="bd" />{eq.data_quality_flag}</span>}
          </div>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <Field label="Serial number" value={eq.serial_number} />
            <Field label="Asset number" value={eq.asset_number} />
            <Field label="Issue number" value={eq.issue_number} />
            <Field label="Part number" value={eq.part_number} />
            <Field label="Internal ref" value={eq.internal_equipment_ref} />
            <Field label="Cal. period (days)" value={eq.calibration_period_days} />
            <Field label="Last calibration" value={fmtDate(eq.last_calibration_date)} />
            <Field label="Next due" value={eq.scrapped ? "Unserviceable" : fmtDate(eq.calibration_due_date)} />
            <Field label="Last delivery note" value={eq.delivery_note_number} />
          </div>
          {dnJobs.length > 0 && (
            <div className="notice" style={{ marginBottom: 16 }} data-testid="equip-dn-job-links">
              <Briefcase size={16} />
              <span>
                {dnJobs.length === 1 ? "Job found for this DN — " : `${dnJobs.length} jobs share this DN — `}
                {dnJobs.map((j, i) => (
                  <React.Fragment key={j.id}>
                    {i > 0 && " · "}
                    <button className="btn-link" onClick={() => onOpenJob(j.id)} data-testid={`equip-open-job-${j.id}`}
                      style={{ background: "none", border: 0, padding: 0, color: "var(--gold-deep)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                      Open DN {j.delivery_note_number} ({j.item_count} item{j.item_count === 1 ? "" : "s"}, {j.customer_name || "—"}) to close out →
                    </button>
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          {eq.scrapped && <div className="notice" style={{ marginBottom: 14 }}><Info size={16} /><span>Unserviceable — excluded from all due/recall reports. Reason: {eq.scrapped_reason || "—"}{eq.scrapped_customer_facing_notes ? `. ${eq.scrapped_customer_facing_notes}` : ""}</span></div>}
          <div className="sec-h"><h3 style={{ fontSize: 14 }}>Calibration history</h3></div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>DN #</th><th>Certificate / CR #</th><th>Cal date</th><th>Due</th><th>Result</th><th>Technician</th><th>Readings</th></tr></thead>
              <tbody>
                {(eq.calibration_history || []).length === 0 && <tr><td colSpan={7} className="t-mut">No calibration events recorded.</td></tr>}
                {(eq.calibration_history || []).slice(0, 40).map((h) => {
                  const hasReadings = Array.isArray(h.readings) && h.readings.length > 0;
                  const isOpen = openReadings === h.id;
                  return (
                    <React.Fragment key={h.id}>
                      <tr>
                        <td className="mono t-mut" data-testid={`cal-history-dn-${h.id}`}>{h.delivery_note_number || "—"}</td>
                        <td className="mono t-strong" data-testid={`cal-history-cr-${h.id}`}>{h.certificate_number}</td>
                        <td className="mono t-mut">{fmtDate(h.calibration_date)}</td>
                        <td className="mono t-mut">{fmtDate(h.calibration_due_date)}</td>
                        <td className="t-mut">{h.result || "—"}</td>
                        <td className="t-mut">{h.technician_name || "—"}</td>
                        <td>
                          {hasReadings ? (
                            <button className="btn ghost sm" data-testid={`readings-toggle-${h.id}`}
                              onClick={() => setOpenReadings(isOpen ? null : h.id)}>
                              {isOpen ? "Hide" : "View"}
                            </button>
                          ) : <span className="t-mut">—</span>}
                        </td>
                      </tr>
                      {isOpen && hasReadings && (
                        <tr><td colSpan={7} style={{ background: "var(--card-2,#faf9f6)" }}>
                          <div style={{ overflowX: "auto" }} data-testid={`readings-view-${h.id}`}>
                            <table className="readings-view">
                              <tbody>
                                {h.readings.map((row, ri) => (
                                  <tr key={ri}>
                                    {row.map((c, ci) => <td key={ci}>{c || ""}</td>)}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {docs.length > 0 && (
            <>
              <div className="sec-h" style={{ marginTop: 18 }}>
                <h3 style={{ fontSize: 14 }}>Reports &amp; documents</h3>
              </div>
              <div style={{ display: "grid", gap: 8 }} data-testid="equip-documents">
                {docs.map((d) => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8,
                    background: "var(--card)" }} data-testid={`equip-doc-${d.id}`}>
                    <span className="ico b-red" style={{ flex: "0 0 auto" }}><FileText size={15} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-strong" style={{ fontSize: 13 }}>{d.title}</div>
                      <div className="t-mut" style={{ fontSize: 11.5 }}>
                        {d.report_number ? `${d.report_number} · ` : ""}{d.description}
                        {d.delivery_note_number ? ` · DN ${d.delivery_note_number}` : ""} · {fmtDate(d.created_at)}
                      </div>
                    </div>
                    <button className="btn ghost sm" onClick={() => downloadDoc(d.id)}
                      data-testid={`equip-doc-download-${d.id}`}>
                      <Download size={12} /> Download
                    </button>
                    {isCustomer && d.doc_type === "non_conforming_report" && (
                      <button className="btn gold sm" onClick={() => requestReplacement(d)}
                        disabled={quoting === d.id} data-testid={`equip-doc-quote-${d.id}`}>
                        <Send size={12} /> {quoting === d.id ? "Sending…" : "Request replacement quote"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {mode === "edit" && (
        <>
          <div className="notice" style={{ marginBottom: 16 }}><Info size={16} /><span>Corrections to serial, asset, part, description, manufacturer, period & customer are written to the audit log. Changing the period recomputes the next due date (unless you set Next due manually below).{isManager ? " Last calibration, Next due & Last delivery note are manager-only corrections." : ""}</span></div>
          <div className="fld" style={{ position: "relative", marginBottom: 16 }}>
            <label>Customer</label>
            {form.customer_id ? (
              <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid="equip-edit-customer-selected">
                <span className="t-strong" style={{ fontSize: 13 }}>
                  {form.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", marginRight: 8 }}>{form.customer_number}</span>}
                  {form.customer_name}
                </span>
                <button type="button" className="icon-btn" style={{ width: 22, height: 22 }}
                        onClick={() => { setForm({ ...form, customer_id: null, customer_name: "", customer_number: "" }); setCustQ(""); }}
                        data-testid="equip-edit-customer-clear"><X size={13} /></button>
              </div>
            ) : (
              <>
                <div className="input" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Search size={13} color="var(--text-3)" />
                  <input style={{ border: "none", outline: "none", width: "100%", background: "none" }}
                    placeholder="Search customer to re-assign…" value={custQ} onChange={(e) => setCustQ(e.target.value)}
                    data-testid="equip-edit-customer-search" />
                </div>
                {custResults.length > 0 && (
                  <div className="card" style={{ position: "absolute", zIndex: 10, top: 62, left: 0, right: 0, maxHeight: 260, overflowY: "auto" }}>
                    {custResults.map((c) => (
                      <button key={c.id} type="button" className="nav-item" style={{ color: "var(--text)", borderRadius: 0, gap: 8, width: "100%" }}
                        onClick={() => { setForm({ ...form, customer_id: c.id, customer_name: c.customer_name, customer_number: c.customer_number }); setCustResults([]); setCustQ(""); }}
                        data-testid={`equip-edit-customer-opt-${c.id}`}>
                        {c.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", fontSize: 11.5, minWidth: 60 }}>{c.customer_number}</span>}
                        <span>{c.customer_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="form-grid">
            {[["description", "Description"], ["manufacturer", "Manufacturer"], ["part_number", "Part number"], ["serial_number", "Serial number"], ["asset_number", "Asset number"], ["issue_number", "Issue number"], ["calibration_period_days", "Cal. period (days)"]].map(([k, l]) => (
              <div className="fld" key={k}><label>{l}</label><input className="input" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`equip-edit-${k}`} /></div>
            ))}
            {isManager && (
              <>
                <div className="fld"><label>Last calibration <span className="t-mut" style={{ fontWeight: 400 }}>(manager only)</span></label>
                  <input className="input" type="date" value={(form.last_calibration_date || "").slice(0, 10)}
                    onChange={(e) => setForm({ ...form, last_calibration_date: e.target.value })}
                    data-testid="equip-edit-last_calibration_date" /></div>
                <div className="fld"><label>Next due <span className="t-mut" style={{ fontWeight: 400 }}>(manager only)</span></label>
                  <input className="input" type="date" value={(form.calibration_due_date || "").slice(0, 10)}
                    onChange={(e) => setForm({ ...form, calibration_due_date: e.target.value })}
                    data-testid="equip-edit-calibration_due_date" /></div>
                <div className="fld"><label>Last delivery note <span className="t-mut" style={{ fontWeight: 400 }}>(manager only)</span></label>
                  <input className="input mono" value={form.delivery_note_number ?? ""}
                    onChange={(e) => setForm({ ...form, delivery_note_number: e.target.value })}
                    data-testid="equip-edit-delivery_note_number" /></div>
              </>
            )}
          </div>
        </>
      )}
      {mode === "scrap" && (
        <>
          <div className="notice" style={{ marginBottom: 16 }}><Info size={16} /><span>This item will be removed from all future calibration-due & recall reports, but kept permanently in history and shown to the customer as <b>Unserviceable</b>. A reason is required and goes to the audit log.</span></div>
          <div className="fld"><label>Reason for scrapping <span className="req">*</span></label>
            <select className="input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="equip-scrap-reason">
              {["Beyond economical repair", "Out of tolerance — unrepairable", "Physical damage", "Obsolete / parts unavailable", "Customer requested disposal", "Other…"].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          {reason === "Other…" && (
            <div className="fld" style={{ marginTop: 10 }}>
              <label>Describe the reason <span className="req">*</span></label>
              <textarea className="input" rows={3} value={otherReason} onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Explain why this equipment is unserviceable…" data-testid="equip-scrap-other-reason" />
            </div>
          )}
        </>
      )}
    </Modal>

    {movingToJob && (
      <MoveEquipmentToJobModal equipment={eq}
        onClose={() => setMovingToJob(false)}
        onSaved={() => { onChanged && onChanged(); }} />
    )}
    </>
  );
}
